// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What this browser remembers about your games (M3-39).
//
// Scores, the last three replays, and one interrupted game. **All of it local, none of it an
// identity** — 08 §1 promises an anonymous visitor creates no account and no server record, and
// the honest way to keep that promise while still keeping their score is to keep it here.
//
// **A resumable game is a replay that has not finished yet.** That sentence is the whole design.
// The obvious alternative is a snapshot — ball positions, rule variables, timers, lamp phases,
// RNG state — which means writing a second description of the simulation, keeping it in step with
// the first for ever, and finding out it has drifted only when somebody's resumed game scores
// differently from the one they left. Re-running the trace has one description and cannot drift,
// and 600,000 steps of arithmetic with no rendering is well under a second: cheaper than the code
// to avoid it, and it is the same code the server will use to validate the score (FR-034).
//
// **Three replays, and the number is a decision rather than a placeholder.** A trace is small but
// not free, an origin's quota is shared with every other page on it, and the value of an old
// replay falls off a cliff — the one you want to watch back is the one that just happened. M4
// moves the interesting ones to an account, where they are worth keeping properly.

import { MAX_TRACE_STEPS, type ReplayTrace } from '@core/replay'
import { openDocuments, type DocumentStore } from './db'

/** How many finished games this browser keeps. See the header. */
export const MAX_REPLAYS = 3

/** How many rows a local high-score table holds. The same five a machine's own table does. */
export const MAX_LOCAL_SCORES = 5

const SCORES_KEY = (machineId: string): string => `scores/${machineId}`
const RESUME_KEY = (machineId: string): string => `resume/${machineId}`
const REPLAY_PREFIX = 'replay/'

/** One finished game, as a table row. */
export interface LocalScore {
  readonly machineId: string
  readonly initials: string
  readonly score: number
  /** Milliseconds since the epoch, from the *browser's* clock — never from the simulation. */
  readonly at: number
  /** How it was played, for 08's leaderboard filter and for a player's own curiosity. */
  readonly inputMethod: string
}

/**
 * The ball number of a game stored before the ball was recorded beside it.
 *
 * Zero rather than `undefined` so the shape is one shape: an optional property would put an
 * `exactOptionalPropertyTypes` dance in every consumer for a value that has one meaning.
 */
export const BALL_UNKNOWN = 0

/** A game the player left, and the ball they left it on. */
export interface InterruptedGame {
  readonly trace: ReplayTrace
  /** The game's own `currentBall` when it was written down, or `BALL_UNKNOWN`. */
  readonly ball: number
}

/** A finished game kept for watching back, with the row it produced. */
export interface StoredReplay {
  readonly id: string
  readonly at: number
  readonly trace: ReplayTrace
}

/**
 * The browser's memory of your games.
 *
 * Every method answers with something usable when there is no store at all — an empty table, a
 * `false`, an `undefined`. Nothing above this has a failure path to write, which is the same rule
 * `local.ts` follows and the reason neither of them can take the page down.
 */
export class LocalHistory {
  private readonly store: DocumentStore | undefined

  private constructor(store: DocumentStore | undefined) {
    this.store = store
  }

  /** Open the store. Resolves to a working history or to one that quietly remembers nothing. */
  static async open(store?: DocumentStore): Promise<LocalHistory> {
    return new LocalHistory(store ?? (await openDocuments()))
  }

  /** Is anything actually being kept? The settings page says so rather than implying it. */
  get available(): boolean {
    return this.store !== undefined
  }

  // --- scores ---------------------------------------------------------------------------

  /** This machine's local table, best first. */
  async scores(machineId: string): Promise<readonly LocalScore[]> {
    const rows = await this.store?.get<LocalScore[]>(SCORES_KEY(machineId))
    return Array.isArray(rows) ? rows : []
  }

  /**
   * Add a finished game to the table, and say whether it made it.
   *
   * Sorted descending with ties keeping the earlier entry — the convention, and also the only
   * stable answer, which is the same rule `Game.recordHighScore` follows for the machine's own
   * table. The two tables are deliberately separate: the machine's resets when the machine does,
   * and this one is the browser's.
   */
  async addScore(row: LocalScore): Promise<boolean> {
    if (this.store === undefined) return false
    const rows = [...(await this.scores(row.machineId))]
    rows.push(row)
    rows.sort((a, b) => b.score - a.score || a.at - b.at)
    rows.length = Math.min(rows.length, MAX_LOCAL_SCORES)
    // Whether it *made the table* is the interesting answer, not whether the write worked: a
    // score that fell off the bottom is a real outcome and the page says so.
    const made = rows.includes(row)
    const written = await this.store.put(SCORES_KEY(row.machineId), rows)
    return written && made
  }

  // --- replays --------------------------------------------------------------------------

  /** The kept replays, newest first. */
  async replays(): Promise<readonly StoredReplay[]> {
    if (this.store === undefined) return []
    const keys = (await this.store.keys()).filter((k) => k.startsWith(REPLAY_PREFIX))
    const found: StoredReplay[] = []
    for (const key of keys) {
      const one = await this.store.get<StoredReplay>(key)
      if (one !== undefined) found.push(one)
    }
    return found.sort((a, b) => b.at - a.at)
  }

  /**
   * Keep a finished game, dropping the oldest past the cap.
   *
   * Trimmed on write rather than on read, so a browser that stops being used does not sit on a
   * pile of traces for ever — and so the quota this occupies is bounded by a number in this file
   * rather than by how much somebody played.
   */
  async addReplay(trace: ReplayTrace, at: number): Promise<boolean> {
    if (this.store === undefined) return false
    const id = `${REPLAY_PREFIX}${at}`
    const ok = await this.store.put(id, { id, at, trace } satisfies StoredReplay)
    const kept = await this.replays()
    for (const old of kept.slice(MAX_REPLAYS)) await this.store.remove(old.id)
    return ok
  }

  // --- the interrupted game -----------------------------------------------------------

  /**
   * Remember a game in progress.
   *
   * Written as the trace it is, so resuming is `replayTrace` and nothing else. Overwrites the
   * previous one: there is exactly one interrupted game per machine, because starting a second
   * is what abandoning the first means.
   *
   * **The ball is recorded beside the trace rather than counted out of it.** A trace holds
   * controls, never consequences, so which ball a player was on is not in there — and the
   * obvious substitute, counting launch events, is wrong in three separate ways: the press that
   * *starts* the game is a launch, a ball save puts the ball back in the lane for another one,
   * and a plunger pulled twice is two. The number the page shows has to be the game's own, taken
   * from the game at the moment it was written down.
   */
  async saveInProgress(trace: ReplayTrace, ball: number): Promise<boolean> {
    if (this.store === undefined) return false
    return this.store.put(RESUME_KEY(trace.machineId), { trace, ball } satisfies InterruptedGame)
  }

  /**
   * The interrupted game, if there is one worth offering.
   *
   * A trace whose machine version has moved on is **not** offered, and that is not caution: the
   * whole reason a resume works is that re-running the input produces the identical game, and a
   * machine whose geometry or rules have changed produces a different one. Offering it would
   * resume somebody into a game that is not the one they left. Keep the original record until
   * another game explicitly replaces it or the player clears history; a lookup must not erase it.
   *
   * **A trace past `MAX_TRACE_STEPS` is refused for exactly the same reason.** `TraceRecorder`
   * stops keeping actions at the two-hour cap — deliberately, since 10 §3 rejects an oversize
   * trace — while `mark` goes on advancing `steps` for as long as the game runs. Past the cap the
   * record therefore holds a *truncated* input list against a step count that keeps growing, and
   * replaying it produces a game that diverges from the one the player was in when the recording
   * stopped. `restore` also clears the recorder's own `capped` flag, so a resumed over-length game
   * would start recording again on top of a list that had already lost events. It is what bounds
   * the resume replay too: the record is re-simulated in full at every launch.
   *
   * A record written by a build that stored the bare trace is still honoured — it resumes
   * identically, and the only thing missing is the ball number, which the offer then leaves out
   * rather than guesses at. One save later it is there, because a game in progress is written
   * down every few seconds.
   */
  async resumable(machineId: string, machineVersion: string): Promise<InterruptedGame | undefined> {
    const stored = await this.store?.get<InterruptedGame | ReplayTrace>(RESUME_KEY(machineId))
    if (stored === undefined) return undefined
    const found: InterruptedGame =
      'trace' in stored ? stored : { trace: stored, ball: BALL_UNKNOWN }
    if (found.trace.machineVersion !== machineVersion) return undefined
    if (found.trace.steps > MAX_TRACE_STEPS) {
      await this.clearInProgress(machineId)
      return undefined
    }
    return found.trace.steps > 0 ? found : undefined
  }

  async clearInProgress(machineId: string): Promise<void> {
    await this.store?.remove(RESUME_KEY(machineId))
  }

  /**
   * Throw everything away.
   *
   * Offered because a page that keeps things on somebody's device with no way to clear them is
   * the thing this product's storage policy exists to avoid — and because an anonymous player
   * has no account to delete instead. Only this product's own keys, found by prefix: the store
   * is ours, but deleting the database wholesale is a thing to do to a database rather than to
   * a player's request.
   */
  async forget(machineId: string): Promise<void> {
    if (this.store === undefined) return
    await this.store.remove(SCORES_KEY(machineId))
    await this.clearInProgress(machineId)
    for (const key of await this.store.keys()) {
      if (key.startsWith(REPLAY_PREFIX)) await this.store.remove(key)
    }
  }
}
